// frontend/src/components/pages/chat/DownloadResponse.jsx
import React, { useState, useRef } from "react";
import { Download } from "lucide-react";
import html2pdf from "html2pdf.js";
import { htmlToText } from "html-to-text";
import {
  Document,
  Packer,
  Paragraph,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  TextRun,
} from "docx";
import { marked } from "marked";
import {
  FaFilePdf,
  FaFileWord,
  FaFileAlt,
  FaFileCode,
} from "react-icons/fa";

const DownloadResponse = ({ content, fileName = "response", messageRef }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Download as TXT (plain text)
  const handleDownloadTXT = () => {
    let text = content;
    if (messageRef && messageRef.current) {
      text = htmlToText(messageRef.current.innerHTML, { wordwrap: 130 });
    }
    const blob = new Blob([text], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setOpen(false);
  };

  // Download as PDF using the actual DOM node
  const handleDownloadPDF = () => {
    if (messageRef && messageRef.current) {
      const originalClass = document.documentElement.className;
      document.documentElement.className = 'light';
      html2pdf()
        .from(messageRef.current)
        .set({
          margin: 10,
          filename: `${fileName}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .save()
        .then(() => {
          document.documentElement.className = originalClass;
        });
    }
    setOpen(false);
  };

  // Download as DOCX using docx
  function markdownToDocxBlocks(md) {
    const tokens = marked.lexer(md);
    const blocks = [];
    tokens.forEach((token) => {
      if (token.type === "heading") {
        blocks.push(
          new Paragraph({
            text: token.text,
            heading: [
              null,
              HeadingLevel.HEADING_1,
              HeadingLevel.HEADING_2,
              HeadingLevel.HEADING_3,
              HeadingLevel.HEADING_4,
              HeadingLevel.HEADING_5,
              HeadingLevel.HEADING_6,
            ][token.depth],
          })
        );
      } else if (token.type === "paragraph") {
        blocks.push(new Paragraph(token.text));
      } else if (token.type === "code") {
        blocks.push(
          new Paragraph({
            children: [
              new TextRun({
                text: token.text,
                font: "Consolas",
                size: 22,
                color: "222222",
              }),
            ],
          })
        );
      } else if (token.type === "table") {
        const rows = [
          new TableRow({
            children: token.header.map(
              (cell) =>
                new TableCell({ children: [new Paragraph({ text: cell })] })
            ),
          }),
          ...token.rows.map(
            (row) =>
              new TableRow({
                children: row.map(
                  (cell) =>
                    new TableCell({ children: [new Paragraph({ text: cell })] })
                ),
              })
          ),
        ];
        blocks.push(new Table({ rows }));
      } else if (token.type === "list") {
        token.items.forEach((item) => {
          blocks.push(new Paragraph({ text: item.text, bullet: { level: 0 } }));
        });
      }
    });
    return blocks;
  }

  const handleDownloadDOCX = async () => {
    try {
      const docBlocks = markdownToDocxBlocks(content);
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: docBlocks.length ? docBlocks : [new Paragraph(content)],
          },
        ],
      });
      const blob = await Packer.toBlob(doc);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      alert("Failed to export DOCX. Try again or contact support.");
    }
    setOpen(false);
  };

  // Download as Markdown (.md)
  const handleDownloadMD = () => {
    const blob = new Blob([content], { type: "text/markdown" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setOpen(false);
  };

  return (
    <div
      className="download-response"
      style={{ position: "relative", display: "inline-block" }}
      ref={dropdownRef}
    >
      <button
        className="download-btn p-1.5 text-fg-60 hover:text-fg-50 hover:bg-bg-50 rounded transition-colors"
        title="Download as"
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        <Download size={16} />
      </button>
      {open && (
        <div className="absolute left-0 bottom-full mb-2 z-50 bg-bg-60 rounded-xl shadow-lg border border-bd-50 min-w-[180px] overflow-hidden">
          <div className="px-4 py-2 text-xs font-normal text-fg-60 bg-bg-60">
            Download as
          </div>
          <div className="py-1">
            <button
              onClick={handleDownloadTXT}
              className="w-full px-4 py-2 text-left text-sm font-normal flex items-center gap-2 text-fg-50 hover:bg-bg-50 transition-colors duration-150"
            >
              <FaFileAlt className="text-fg-60" />
              <span>Text</span>
            </button>
            <button
              onClick={handleDownloadPDF}
              className="w-full px-4 py-2 text-left text-sm font-normal flex items-center gap-2 text-fg-50 hover:bg-bg-50 transition-colors duration-150"
            >
              <FaFilePdf className="text-red-400" />
              <span>PDF</span>
            </button>
            <button
              onClick={handleDownloadMD}
              className="w-full px-4 py-2 text-left text-sm font-normal flex items-center gap-2 text-fg-50 hover:bg-bg-50 transition-colors duration-150"
            >
              <FaFileCode className="text-green-400" />
              <span>Markdown</span>
            </button>
            <button
              onClick={handleDownloadDOCX}
              className="w-full px-4 py-2 text-left text-sm font-normal flex items-center gap-2 text-fg-50 hover:bg-bg-50 transition-colors duration-150"
            >
              <FaFileWord className="text-blue-400" />
              <span>Word</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadResponse;
